// src/components/MessageList.jsx
import React, { useMemo } from 'react';
import TypingIndicator from '../TypingIndicator/TypingIndicator';
import './MessageList.css';
import { Lightbulb, ClipboardCheck } from 'lucide-react';
import { getVoiceProfile } from '../../config/voiceProfiles';
function renderQuoted(text) {
  const parts = [];
  const allMatches = [];

  for (const match of text.matchAll(/\*\*([^*]+)\*\*/g)) {
    allMatches.push({ type: "bold", start: match.index, end: match.index + match[0].length, text: match[1] });
  }
  for (const match of text.matchAll(/"(.*?)"/g)) {
    allMatches.push({ type: "quote", start: match.index, end: match.index + match[0].length, text: match[1] });
  }

  // Sort by start position
  allMatches.sort((a, b) => a.start - b.start);

  // Filter out overlaps (e.g., quote inside bold)
  const filtered = [];
  for (const m of allMatches) {
    if (!filtered.length || m.start >= filtered[filtered.length - 1].end) filtered.push(m);
  }

  let cursor = 0;
  filtered.forEach((m, idx) => {
    if (cursor < m.start) parts.push(text.slice(cursor, m.start));
    if (m.type === "bold") {
      parts.push(<strong key={`b-${idx}`}>{m.text}</strong>);
    } else if (m.type === "quote") {
      parts.push(<strong key={`q-${idx}`} className="ai-quote">"{m.text}"</strong>);
    }
    cursor = m.end;
  });
  if (cursor < text.length) parts.push(text.slice(cursor));

  return parts.length ? parts : text;
}

function renderAi(ai) {
  const lines = (ai || '').split(/\n+/);
  const out = [];
  let i = 0;


  while (i < lines.length) {
    const line = lines[i].trim();
    // Handle "Tip: Do this right now." as single-line tip
    const SINGLE_LINE_TIP = /^tip[:\-—–]\s*(.+)$/i;
    const singleLineTipMatch = line.match(SINGLE_LINE_TIP);
    if (singleLineTipMatch) {
      out.push(
        <div key={`tip-${i}`} className="ai-callout tip" role="note" aria-label="AI Tip">
          <div className="ai-callout-container">
            <div className="ai-callout-icon">
              <Lightbulb size={20} aria-hidden="true" />
            </div>
            <span className="ai-callout-kicker">Tip</span>
          </div>
          <div className="ai-callout-body">
            <div className="ai-callout-text">{renderQuoted(singleLineTipMatch[1])}</div>
          </div>
        </div>
      );
      i++;
      continue;
    }
    // TIP block
    if (/^tip(?:[:\-—–])?\s*$/i.test(line)) {
      let tipLines = [];
      i++;
      while (i < lines.length && (lines[i].startsWith('-') || lines[i].match(/^\d+[.)]/) || lines[i].trim())) {
        // Stop at next header or empty line, but allow bullet/number lines
        if (/^(practice|try this|exercise|drill)[:\-—–]?\s*$/i.test(lines[i].trim())) break;
        if (lines[i].trim() === '') break;
        tipLines.push(lines[i].replace(/^[-\d.)\s]*/, '').trim());
        i++;
      }
      // Decide: bulleted, numbered, or just lines
      const isBulleted = tipLines.every(l => l.trim().startsWith('-'));
      const isNumbered = tipLines.every(l => /^\d+[.)]/.test(l.trim()));
      out.push(
        <div key={`tip-${i}`} className="ai-callout tip" role="note" aria-label="AI Tip">
          <div className="ai-callout-container">
            <div className="ai-callout-icon">
              <Lightbulb size={20} aria-hidden="true" />
            </div>

            <span className="ai-callout-kicker">Tip</span>
          </div>
          <div className="ai-callout-body">

            <div className="ai-callout-text">
              {isBulleted ? (
                <ul className="ai-callout-list">
                  {tipLines.map((txt, idx) => (
                    <li key={idx}>{renderQuoted(txt.replace(/^-\s*/, ''))}</li>
                  ))}
                </ul>
              ) : isNumbered ? (
                <ol className="ai-callout-list">
                  {tipLines.map((txt, idx) => (
                    <li key={idx}>{renderQuoted(txt.replace(/^\d+[.)]\s*/, ''))}</li>
                  ))}
                </ol>
              ) : (
                tipLines.map((txt, idx) => <div key={idx}>{renderQuoted(txt)}</div>)
              )}
            </div>
          </div>
        </div>
      );
      continue;
    }

    const SINGLE_LINE_PRACTICE = /^(practice|try this|exercise|drill)[:\-—–]\s*(.+)$/i;
    const singleLinePracticeMatch = line.match(SINGLE_LINE_PRACTICE);
    if (singleLinePracticeMatch) {
      out.push(
        <div key={`practice-${i}`} className="ai-callout practice" role="note" aria-label="Practice">
          <div className="ai-callout-container">
            <div className="ai-callout-icon">
              <ClipboardCheck size={20} aria-hidden="true" />
            </div>
            <span className="ai-callout-kicker">Practice</span>
          </div>
          <div className="ai-callout-body">

            <div className="ai-callout-text">{renderQuoted(singleLinePracticeMatch[2])}</div>
          </div>
        </div>
      );
      i++;
      continue;
    }
    // PRACTICE block
    if (/^(practice|try this|exercise|drill)[:\-—–]?$/i.test(line)) {

      let practiceLines = [];
      i++;
      while (
        i < lines.length &&
        (lines[i].trim().startsWith('-') || /^\d+[.)]/.test(lines[i].trim()))
      ) {
        practiceLines.push(lines[i]);
        i++;
      }
      const isBulleted = practiceLines.every(l => l.trim().startsWith('-'));
      const isNumbered = practiceLines.every(l => /^\d+[.)]/.test(l.trim()));
      out.push(
        <div key={`practice-${i}`} className="ai-callout practice" role="note" aria-label="Practice">
          <div className="ai-callout-container">
            <div className="ai-callout-icon">
              <ClipboardCheck size={20} aria-hidden="true" />
            </div>
            <span className="ai-callout-kicker">Practice</span>
          </div>
          <div className="ai-callout-body">

            <div className="ai-callout-text">
              {isBulleted ? (
                <ul className="ai-callout-list">
                  {practiceLines.map((txt, idx) => (
                    <li key={idx}>{renderQuoted(txt.replace(/^-\s*/, ''))}</li>
                  ))}
                </ul>
              ) : isNumbered ? (
                <ol className="ai-callout-list">
                  {practiceLines.map((txt, idx) => (
                    <li key={idx}>{renderQuoted(txt.replace(/^\d+[.)]\s*/, ''))}</li>
                  ))}
                </ol>
              ) : (
                practiceLines.map((txt, idx) => <div key={idx}>{renderQuoted(txt)}</div>)
              )}
            </div>
          </div>
        </div>
      );
      continue;
    }

    // Otherwise, regular paragraph
    if (line) {
      out.push(<p key={`p-${i}`} className="ai-paragraph">{renderQuoted(line)}</p>);
    }
    i++;
  }
  return out;
}



export function ConversationTurn({ aiPartnerLabel = "Response", turn }) {
  if (!turn?.user && !turn?.ai) return null;

  return (
    <div className="message-pair">
      {turn.user ? (
        <div className="transcript user-message">
          <p>{turn.user}</p>
        </div>
      ) : null}
      {turn.ai ? (
        <div className="response ai-message">
          <strong className="ai-speaker-label">{aiPartnerLabel}</strong>
          <div className="response-body">{renderAi(turn.ai)}</div>
        </div>
      ) : null}
    </div>
  );
}

export default function MessageList({
  conversation = [],
  showTyping = true,
  isTrainingMode = false,
  sessionTimeLabel,
  speakingWithLabel = "Response",
  voice,
}) {
  const focusedConversation = useMemo(() => {
    let latestAi = null;
    let latestUser = null;

    conversation.forEach((turn, index) => {
      if (turn?.ai) latestAi = { index, text: turn.ai };
      if (turn?.user) latestUser = { index, text: turn.user };
    });

    return { ai: latestAi, user: latestUser };
  }, [conversation]);

  const lastTurn = conversation[conversation.length - 1];
  const waitingForAi = Boolean(showTyping && lastTurn?.user && !lastTurn?.ai);
  const aiPartnerLabel = isTrainingMode ? "Telome Coach" : speakingWithLabel || "Response";
  const voiceProfile = getVoiceProfile(voice);

  return (
    <div className="message-list">
      <div className="message-list-portrait">
        <img
          alt={`${aiPartnerLabel} voice avatar`}
          className="message-list-portrait-image"
          decoding="async"
          fetchPriority="high"
          src={voiceProfile.image}
        />

        {sessionTimeLabel ? (
          <div className="message-list-image-timer" role="timer">
            {sessionTimeLabel}
          </div>
        ) : null}

        {focusedConversation.ai ? (
          <div
            aria-live="polite"
            className="message-list-ai-overlay"
            key={`focused-ai-${focusedConversation.ai.index}`}
          >
            <strong className="ai-speaker-label">{aiPartnerLabel}</strong>
            <div className="response-body">{renderAi(focusedConversation.ai.text)}</div>
          </div>
        ) : null}
      </div>

      {focusedConversation.user ? (
        <div className="message-list-focused-user" key={`focused-user-${focusedConversation.user.index}`}>
          <p>{focusedConversation.user.text}</p>
        </div>
      ) : null}

      {waitingForAi ? (
        <div className="message-list-typing" aria-live="polite">
          <TypingIndicator />
        </div>
      ) : null}

      {!focusedConversation.ai && !focusedConversation.user ? (
        <div className="message-list-empty">
          <strong>Tap play to start.</strong>
        </div>
      ) : null}
    </div>
  );
}
